import React from 'react';
import {Box, Center, HStack, Spinner, VStack} from '@chakra-ui/react';
import {TextG12, TextG14, TextG22, TextG16} from '../../../components/typography';
import NeighborsInterestedDisplay from '../../../components/UsersList/NeighborsInterestedDisplay';
import {UserEvent} from '../../../hooks/useUserEvents';
import {useNavigate} from 'react-router-dom';
import Modal from '../../../components/Modals/Modal';
import dayjs from 'dayjs';
import MessageEventBtn from './MessageEventBtn';

interface MessageModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: number;
  handle: string;
  events: UserEvent[];
  isLoading?: boolean;
}

const MessageModal = ({isOpen, onClose, userId, handle, events, isLoading}: MessageModalProps) => {
  const navigate = useNavigate();

  const handleMessageInChat = (eventId: number) => {
    onClose();
    navigate(`/event/${eventId}/chat`, {
      state: {mentionUser: {id: userId, handle}},
    });
  };

  const formatDate = (dateStr: string, price: number) => {
    const d = dayjs(dateStr);
    const dateFormatted = d.format('ddd, MMM D [at] h:mm A');
    const priceStr = price === 0 ? 'Free' : `$${price}`;
    return `${dateFormatted} • ${priceStr}`;
  };

  return (
    <Modal
      show={isOpen}
      parentCallback={onClose}
      closeButton
      closeButtonPosition="right"
      header={
        <Box pr={6}>
          <TextG22 fontWeight="700" mt="0" color="#202020">
            Message through a shared event chat
          </TextG22>
          <TextG16 mt="4px" color="#363636" fontWeight="500">
            Messaging is only available through events you both saved. Pick one
            below and we'll notify them in that chat.
          </TextG16>
        </Box>
      }
      scrollBehavior="inside"
      modalPadding="16px 20px 24px">
      <VStack spacing={4} align="stretch" pb={2}>
        {isLoading && events.length === 0 ? (
          <Center py={8}>
            <Spinner size="lg" color="#E02F84" />
          </Center>
        ) : events.length === 0 ? (
          <Box textAlign="center" py={6} px={2}>
            <TextG14 mt="0" fontWeight="600" color="#202020" mb={2}>
              No shared event chats yet
            </TextG14>
            <TextG14 mt="0" color="#757D84">
              You can only message this neighbor through an event you both
              saved. Save some of the same events to start a conversation!
            </TextG14>
          </Box>
        ) : null}
        {events.map((event) => (
          <Box
            key={event.id}
            border="1px solid #E5E5E5"
            borderRadius="16px"
            p={4}>
            <HStack spacing={3} align="flex-start" mb={3}>
              <Box
                w="72px"
                h="72px"
                borderRadius="12px"
                bg="#E5E5E5"
                flexShrink={0}
                overflow="hidden">
                {event.image_url && (
                  <img
                    src={event.image_url}
                    alt={event.title}
                    style={{width: '100%', height: '100%', objectFit: 'cover'}}
                  />
                )}
              </Box>
              <VStack align="flex-start" spacing={1} flex={1} minW={0}>
                <TextG12 mt="0" color="#636363">
                  {formatDate(event.start_date_time, event.price)}
                </TextG12>
                <TextG14 mt="0" fontWeight="700" noOfLines={2}>
                  {event.title}
                </TextG14>
                {event.place?.name && (
                  <HStack spacing={1}>
                    <i className="fi fi-rr-marker" style={{fontSize: '10px', color: '#636363'}} />
                    <TextG12 mt="0" color="#636363" noOfLines={1}>
                      {event.place.name}
                    </TextG12>
                  </HStack>
                )}
              </VStack>
            </HStack>

            <Box mb={3}>
              <NeighborsInterestedDisplay
                articleId={event.id}
                articleType={event.is_meetup ? 'meetup' : 'event'}
                bookmarksCount={event.attendeeCount}
              />
            </Box>

            <MessageEventBtn onClick={() => handleMessageInChat(event.id)}>
              <i className="fi fi-rr-paper-plane" style={{fontSize: '12px'}} />
              Message in Event Chat
            </MessageEventBtn>
          </Box>
        ))}
      </VStack>
    </Modal>
  );
};

export default MessageModal;
